/**
 * «Cambió el plano» (OPUS ci, idea 4): comparar dos revisiones de un plano. Aquí vive la parte PURA y determinista
 * ($0, sin IA): emparejar las piezas de A con las de B y decir qué se agregó, qué se quitó y qué cambió.
 *
 *  - IFC: la identidad de una pieza es su GlobalId. Si el exportador regeneró los GUID (ninguno coincide), se
 *    empareja por lugar y medida y TODAS las parejas salen «dudosas».
 *  - DWG/DXF: no hay identidad estable entre dos archivos; el handle solo cuenta si además coinciden capa y tipo
 *    (en dos dibujos sin relación los handles chocan por casualidad). Lo demás se empareja por geometría.
 *
 * Una pareja «dudosa» o una pieza «ambigua» NUNCA decide sola: quien la usa la marca «revisar».
 *
 * Los umbrales NO viven fijos aquí: están en la calibración del motor (`umbrales.cambio_*`). Esto es el default
 * (provisional) y el respaldo si la calibración no responde.
 */

export interface ParametrosCambioDePlano {
  /** % por debajo del cual una pieza «no cambió de medida» (redondeos del exportador). */
  cambio_tolerancia_medida_pct: number;
  /** m: máxima distancia entre centros para emparejar por geometría dos piezas de otra identidad. */
  cambio_emparejar_distancia_m: number;
  /** %: máxima diferencia de medida principal para emparejar por geometría. */
  cambio_emparejar_medida_pct: number;
  /** m: desde qué desplazamiento del centro una pieza «se movió». */
  cambio_movida_m: number;
  /** %: si más que esto de las entidades de AMBOS dibujos quedó sin pareja, el dibujo se movió o se reescaló entero: no se resalta pieza por pieza. */
  cambio_dwg_desplazado_pct: number;
}

export const PARAMETROS_CAMBIO_DE_PLANO_DEFAULT: Readonly<ParametrosCambioDePlano> = {
  cambio_tolerancia_medida_pct: 0.5,
  cambio_emparejar_distancia_m: 0.05,
  cambio_emparejar_medida_pct: 2,
  cambio_movida_m: 0.01,
  cambio_dwg_desplazado_pct: 50,
};

export const RANGO_PARAMETROS_CAMBIO_DE_PLANO: Readonly<Record<keyof ParametrosCambioDePlano, readonly [number, number]>> = {
  cambio_tolerancia_medida_pct: [0, 100],
  cambio_emparejar_distancia_m: [0, 100],
  cambio_emparejar_medida_pct: [0, 100],
  cambio_movida_m: [0, 100],
  cambio_dwg_desplazado_pct: [0, 100],
};

/** Etiqueta y pista de cada parámetro para el panel de Calibración (el panel no escribe textos propios). */
export const DESCRIPCION_PARAMETROS_CAMBIO_DE_PLANO: Readonly<
  Record<keyof ParametrosCambioDePlano, { etiqueta: string; pista: string; unidad: '%' | 'm'; paso: number }>
> = {
  cambio_tolerancia_medida_pct: { etiqueta: 'Cambió el plano: diferencia de medida que cuenta (%)', pista: 'Por debajo, la pieza se considera igual', unidad: '%', paso: 0.1 },
  cambio_emparejar_distancia_m: { etiqueta: 'Cambió el plano: distancia para emparejar piezas (m)', pista: 'Cuando el modelo cambia los nombres internos, se empareja por lugar. En un DWG, una pieza con el mismo nombre interno más lejos que esto se marca para revisar', unidad: 'm', paso: 0.01 },
  cambio_emparejar_medida_pct: { etiqueta: 'Cambió el plano: diferencia de medida para emparejar (%)', pista: 'Junto con la distancia, para emparejar por lugar y medida', unidad: '%', paso: 0.1 },
  cambio_movida_m: { etiqueta: 'Cambió el plano: desplazamiento que cuenta como «se movió» (m)', pista: 'Por debajo, la pieza no se movió', unidad: 'm', paso: 0.01 },
  cambio_dwg_desplazado_pct: { etiqueta: 'Cambió el plano: % sin pareja para considerar que es otro plano (%)', pista: 'Si más que esto de las piezas de los DOS planos quedó sin pareja, se avisa que el plano nuevo parece otro plano (o movido o a otra escala) y se pregunta si continuar. En un DWG, además, no se marca pieza por pieza', unidad: '%', paso: 1 },
};

/**
 * ¿La revisión nueva parece OTRO plano (o está movida o a otra escala entera)? Sí cuando más que `pct` % de las piezas de AMBOS
 * lados quedó sin pareja. Se usa el MÍNIMO de las dos proporciones para que agregar o quitar una planta entera no la dispare.
 */
export function pareceOtroPlano(p: { piezasA: number; piezasB: number; sinParejaA: number; sinParejaB: number } | null | undefined, pct: number): boolean {
  if (!p || p.piezasA <= 0 || p.piezasB <= 0) return false;
  return Math.min(p.sinParejaA / p.piezasA, p.sinParejaB / p.piezasB) * 100 > pct;
}

/** Completa y sanea lo que venga (calibración, API): clave ausente o fuera de rango → default. Nunca tira. */
export function normalizarParametrosCambioDePlano(x: unknown): ParametrosCambioDePlano {
  const src = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>;
  const out = { ...PARAMETROS_CAMBIO_DE_PLANO_DEFAULT };
  for (const k of Object.keys(out) as (keyof ParametrosCambioDePlano)[]) {
    const v = src[k];
    const [mn, mx] = RANGO_PARAMETROS_CAMBIO_DE_PLANO[k];
    if (typeof v === 'number' && Number.isFinite(v) && v >= mn && v <= mx) out[k] = v;
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------
// Piezas
// ---------------------------------------------------------------------------------------------------

export interface MedidasDePieza {
  longitud?: number;
  area?: number;
  volumen?: number;
  peso?: number;
  /** DWG: cuántos vanos (puertas y ventanas) trae el muro. Es un conteo: se compara EXACTO, no por porcentaje. */
  vanos?: number;
}

/** Una pieza del plano, lista para compararse (la saca cada formato de su propia medición). */
export interface PiezaComparable {
  /** Identidad DENTRO de su revisión (IFC: expressID; DWG: handle). Sirve para citarla, no para emparejar. */
  id: string;
  /** IFC: GlobalId. DWG: handle del dibujo. Es la identidad que se intenta conservar entre revisiones. */
  gid?: string | null;
  /** El tipo (IFC) o la capa/bloque (DWG) al que pertenece: la misma identidad que la selección de un renglón. */
  grupo: string;
  /** Clase canónica (IFC: IfcWall…; DWG: «bloque» / «capa»). Dos piezas de otra clase no se emparejan por lugar. */
  clase: string;
  /** Nivel o planta; si ambas piezas lo traen, deben coincidir para emparejarse por lugar. */
  nivel?: string | null;
  nombre?: string;
  /** Centro de la pieza en m. */
  centro?: readonly [number, number, number] | null;
  /**
   * DWG: la pieza es un AGREGADO (una capa en una planta) y su identidad es su nombre, no un lugar. Si las dos piezas
   * emparejadas por identidad lo traen, no se exige cercanía (un agregado no tiene centro) y la pareja es segura.
   */
  identidadPorNombre?: boolean;
  medidas: MedidasDePieza;
}

export interface ParejaDePiezas {
  a: string;
  b: string;
  por: 'identidad' | 'geometria';
  /** true = no es seguro que sean la misma pieza: nunca cambia sola una selección ni una cantidad. */
  dudoso: boolean;
}

export interface ResultadoEmparejado {
  parejas: ParejaDePiezas[];
  /** Ids de A sin pareja (quitadas). */
  soloA: string[];
  /** Ids de B sin pareja (agregadas). */
  soloB: string[];
  /** Piezas de A con 2 o más candidatas en B: no se emparejan; quedan quitada/agregada, ambas dudosas. */
  ambiguas: { a: string; candidatas: string[] }[];
  /** Los dos modelos traen identidades y NINGUNA coincide: el exportador renombró las piezas. */
  identidadesRegeneradas: boolean;
}

/** Las medidas continuas (se comparan por porcentaje); `vanos` es un conteo y se compara exacto. */
const MEDIDAS = ['area', 'longitud', 'volumen', 'peso'] as const;

/** Diferencia relativa en % entre dos medidas (0 si son iguales; el mayor de las dos como base). */
function difPct(x: number, y: number): number {
  const base = Math.max(Math.abs(x), Math.abs(y));
  return base < 1e-9 ? 0 : (Math.abs(x - y) / base) * 100;
}

/** La mayor diferencia (%) entre las medidas que ambas piezas traen; null si no comparten ninguna. */
function difMedidasPct(a: MedidasDePieza, b: MedidasDePieza): number | null {
  let mayor: number | null = null;
  for (const k of MEDIDAS) {
    const x = a[k];
    const y = b[k];
    if (x == null || y == null) continue;
    const d = difPct(x, y);
    if (mayor == null || d > mayor) mayor = d;
  }
  return mayor;
}

function distancia(a: PiezaComparable, b: PiezaComparable): number | null {
  if (!a.centro || !b.centro) return null;
  return Math.hypot(a.centro[0] - b.centro[0], a.centro[1] - b.centro[1], a.centro[2] - b.centro[2]);
}

/** Identidades que aparecen UNA sola vez en el lado (un GUID repetido no identifica nada). */
function indicePorIdentidad(piezas: readonly PiezaComparable[]): Map<string, PiezaComparable> {
  const vistas = new Map<string, PiezaComparable>();
  const repetidas = new Set<string>();
  for (const p of piezas) {
    const g = p.gid?.trim();
    if (!g) continue;
    if (vistas.has(g)) repetidas.add(g);
    else vistas.set(g, p);
  }
  for (const g of repetidas) vistas.delete(g);
  return vistas;
}

/**
 * Empareja las piezas de A con las de B.
 *  1. Misma identidad (GlobalId en IFC; en DWG el handle, y solo si coinciden también capa y tipo): pareja segura.
 *  2. Las que sobran, por lugar y medida dentro de la misma clase y nivel. Solo se acepta si la candidata es única
 *     en los dos sentidos; entonces la pareja es DUDOSA. Con 2 o más candidatas no se empareja.
 */
export function emparejarPiezas(
  a: readonly PiezaComparable[],
  b: readonly PiezaComparable[],
  p: ParametrosCambioDePlano,
  modo: 'ifc' | 'dwg',
): ResultadoEmparejado {
  const parejas: ParejaDePiezas[] = [];
  const usadasA = new Set<string>();
  const usadasB = new Set<string>();

  const indiceB = indicePorIdentidad(b);
  const indiceA = indicePorIdentidad(a);
  for (const pa of a) {
    const g = pa.gid?.trim();
    if (!g || !indiceA.has(g)) continue;
    const pb = indiceB.get(g);
    if (!pb) continue;
    // DWG: un handle igual no basta (en dos dibujos sin relación chocan por casualidad): capa y tipo deben coincidir.
    if (modo === 'dwg' && (pa.grupo !== pb.grupo || pa.clase !== pb.clase)) continue;
    // DWG: aun con capa y tipo iguales, un handle repetido lejos (o sin lugar para comprobarlo) puede ser otra pieza.
    const porNombre = pa.identidadPorNombre === true && pb.identidadPorNombre === true;
    const lejos = modo === 'dwg' && !porNombre && ((distancia(pa, pb) ?? Infinity) > p.cambio_emparejar_distancia_m);
    parejas.push({ a: pa.id, b: pb.id, por: 'identidad', dudoso: lejos });
    usadasA.add(pa.id);
    usadasB.add(pb.id);
  }

  const hayIdentidades = indiceA.size > 0 && indiceB.size > 0;
  const identidadesRegeneradas = modo === 'ifc' && hayIdentidades && parejas.length === 0;

  const restoA = a.filter((x) => !usadasA.has(x.id));
  const restoB = b.filter((x) => !usadasB.has(x.id));
  const candidatasDe = (x: PiezaComparable, pool: readonly PiezaComparable[]): PiezaComparable[] =>
    pool.filter((y) => {
      if (x.clase !== y.clase) return false;
      if (x.nivel != null && y.nivel != null && x.nivel !== y.nivel) return false;
      const d = distancia(x, y);
      if (d == null || d > p.cambio_emparejar_distancia_m) return false;
      const dm = difMedidasPct(x.medidas, y.medidas);
      return dm == null || dm <= p.cambio_emparejar_medida_pct;
    });

  const deA = new Map<string, PiezaComparable[]>();
  const deB = new Map<string, PiezaComparable[]>();
  for (const x of restoA) deA.set(x.id, candidatasDe(x, restoB));
  for (const y of restoB) deB.set(y.id, candidatasDe(y, restoA));

  const ambiguas: ResultadoEmparejado['ambiguas'] = [];
  for (const x of restoA) {
    const cs = deA.get(x.id)!;
    if (cs.length === 0) continue;
    if (cs.length >= 2) {
      ambiguas.push({ a: x.id, candidatas: cs.map((c) => c.id) });
      continue;
    }
    const y = cs[0]!;
    const inversas = deB.get(y.id) ?? [];
    if (inversas.length === 1 && inversas[0]!.id === x.id) {
      parejas.push({ a: x.id, b: y.id, por: 'geometria', dudoso: true });
      usadasA.add(x.id);
      usadasB.add(y.id);
    } else {
      // La candidata única de A tiene más de una de A que la quieren: tampoco se decide sola.
      ambiguas.push({ a: x.id, candidatas: [y.id] });
    }
  }

  return {
    parejas,
    soloA: a.filter((x) => !usadasA.has(x.id)).map((x) => x.id),
    soloB: b.filter((x) => !usadasB.has(x.id)).map((x) => x.id),
    ambiguas,
    identidadesRegeneradas,
  };
}

// ---------------------------------------------------------------------------------------------------
// Cambios por pieza
// ---------------------------------------------------------------------------------------------------

export type TipoCambioPieza = 'agregada' | 'quitada' | 'cambio_medida' | 'cambio_tipo' | 'movida';

export interface CambioDePieza {
  tipo: TipoCambioPieza;
  /** true = no es seguro (pareja por lugar, o pieza con varias candidatas): la persona lo revisa. */
  dudoso: boolean;
  a: PiezaComparable | null;
  b: PiezaComparable | null;
  /** Frase para la persona («Su área pasó de 12.40 a 14.40 m²»). Sin jerga. */
  detalle: string;
}

const UNIDAD_MEDIDA: Record<(typeof MEDIDAS)[number], string> = { area: 'm²', longitud: 'm', volumen: 'm³', peso: 'kg' };
const NOMBRE_MEDIDA: Record<(typeof MEDIDAS)[number], string> = { area: 'área', longitud: 'largo', volumen: 'volumen', peso: 'peso' };
const f2 = (n: number): string => n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Qué cambió entre las medidas de dos piezas emparejadas (las que superan la tolerancia), en palabras. */
function medidasQueCambiaron(a: MedidasDePieza, b: MedidasDePieza, tolPct: number): string[] {
  const out: string[] = [];
  for (const k of MEDIDAS) {
    const x = a[k];
    const y = b[k];
    if (x == null || y == null) continue;
    if (difPct(x, y) > tolPct) out.push(`su ${NOMBRE_MEDIDA[k]} pasó de ${f2(x)} a ${f2(y)} ${UNIDAD_MEDIDA[k]}`);
  }
  // Un conteo (los vanos) cambia con cualquier diferencia: 4 → 5 no es «un 25 % más», es un vano más.
  if (a.vanos != null && b.vanos != null && a.vanos !== b.vanos) out.push(`sus vanos pasaron de ${a.vanos} a ${b.vanos}`);
  return out;
}

/**
 * La lista de cambios entre dos revisiones, a partir del emparejado. Una pareja que no cambió de tipo, de medida
 * ni de lugar no aparece.
 */
export function cambiosDePiezas(
  a: readonly PiezaComparable[],
  b: readonly PiezaComparable[],
  emparejado: ResultadoEmparejado,
  p: ParametrosCambioDePlano,
): CambioDePieza[] {
  const porIdA = new Map(a.map((x) => [x.id, x] as const));
  const porIdB = new Map(b.map((x) => [x.id, x] as const));
  const ambiguasA = new Set(emparejado.ambiguas.map((x) => x.a));
  const ambiguasB = new Set(emparejado.ambiguas.flatMap((x) => x.candidatas));
  const out: CambioDePieza[] = [];

  for (const par of emparejado.parejas) {
    const pa = porIdA.get(par.a);
    const pb = porIdB.get(par.b);
    if (!pa || !pb) continue;
    if (pa.grupo !== pb.grupo || pa.clase !== pb.clase) {
      out.push({
        tipo: 'cambio_tipo',
        dudoso: par.dudoso,
        a: pa,
        b: pb,
        detalle: pa.clase !== pb.clase ? `Antes era «${pa.grupo}» (${pa.clase}) y ahora es «${pb.grupo}» (${pb.clase})` : `Antes era del tipo «${pa.grupo}» y ahora es de «${pb.grupo}»`,
      });
      continue;
    }
    const medidas = medidasQueCambiaron(pa.medidas, pb.medidas, p.cambio_tolerancia_medida_pct);
    if (medidas.length > 0) {
      const frase = medidas.join(' y ');
      out.push({ tipo: 'cambio_medida', dudoso: par.dudoso, a: pa, b: pb, detalle: frase.charAt(0).toLocaleUpperCase('es-MX') + frase.slice(1) });
      continue;
    }
    const d = distancia(pa, pb);
    if (d != null && d > p.cambio_movida_m) {
      out.push({ tipo: 'movida', dudoso: par.dudoso, a: pa, b: pb, detalle: `Se movió ${f2(d)} m sin cambiar de medida` });
    }
  }
  for (const id of emparejado.soloA) {
    const pa = porIdA.get(id);
    if (pa) out.push({ tipo: 'quitada', dudoso: ambiguasA.has(id), a: pa, b: null, detalle: 'Ya no está en la revisión nueva' });
  }
  for (const id of emparejado.soloB) {
    const pb = porIdB.get(id);
    if (pb) out.push({ tipo: 'agregada', dudoso: ambiguasB.has(id), a: null, b: pb, detalle: 'Es nueva en la revisión' });
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------
// Vistas (plantas) de un DWG
// ---------------------------------------------------------------------------------------------------

/** Una vista de un DWG, lista para emparejarse (sale de `PlanoVista`). Su id es POSICIONAL ('v1', 'v2'…). */
export interface VistaComparable {
  id: string;
  rotulo: string | null;
  tipo: string;
  clase?: string | null;
  ancho_m: number | null;
  alto_m: number | null;
}

export interface ParejaDeVistas {
  a: string;
  b: string;
  por: 'rotulo' | 'forma' | 'orden';
  /** true = no es seguro (no se emparejó por su rótulo): nada cambia solo. */
  dudoso: boolean;
}

export interface ResultadoVistas {
  parejas: ParejaDeVistas[];
  soloA: string[];
  soloB: string[];
}

const normalizarRotulo = (s: string | null | undefined): string | null => {
  const t = (s ?? '').normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/\s+/g, ' ').trim();
  return t || null;
};

/**
 * Empareja las vistas de dos revisiones de un DWG. El id de una vista es su POSICIÓN ('v1' = la de más a la
 * izquierda): si la revisión agrega o reordena una planta, el mismo id es otra planta. Por eso:
 *  1. Mismo rótulo (sin acentos, mayúsculas ni espacios de más), único en los dos lados: pareja segura.
 *  2. Lo que sobra, por tipo y tamaño: candidata única en los dos sentidos → pareja dudosa.
 *  3. Si aún sobran y en ese tipo quedan las mismas en los dos lados, en su orden → pareja dudosa.
 * Lo demás queda sin pareja.
 */
export function emparejarVistas(
  a: readonly VistaComparable[],
  b: readonly VistaComparable[],
  p: ParametrosCambioDePlano,
): ResultadoVistas {
  const parejas: ParejaDeVistas[] = [];
  const usadasA = new Set<string>();
  const usadasB = new Set<string>();
  const unicos = (vs: readonly VistaComparable[]) => {
    const m = new Map<string, VistaComparable | null>();
    for (const v of vs) {
      const r = normalizarRotulo(v.rotulo);
      if (r) m.set(r, m.has(r) ? null : v);
    }
    return m;
  };
  const ua = unicos(a);
  const ub = unicos(b);
  for (const [r, va] of ua) {
    const vb = ub.get(r);
    if (!va || !vb) continue;
    parejas.push({ a: va.id, b: vb.id, por: 'rotulo', dudoso: false });
    usadasA.add(va.id);
    usadasB.add(vb.id);
  }
  const forma = (v: VistaComparable) => `${v.tipo}|${v.clase ?? ''}`;
  const tamanoParecido = (x: VistaComparable, y: VistaComparable) => {
    if (x.ancho_m == null || y.ancho_m == null || x.alto_m == null || y.alto_m == null) return true;
    return difPct(x.ancho_m, y.ancho_m) <= p.cambio_emparejar_medida_pct && difPct(x.alto_m, y.alto_m) <= p.cambio_emparejar_medida_pct;
  };
  const restoA = () => a.filter((v) => !usadasA.has(v.id));
  const restoB = () => b.filter((v) => !usadasB.has(v.id));
  const cand = (x: VistaComparable, pool: readonly VistaComparable[]) => pool.filter((y) => forma(x) === forma(y) && tamanoParecido(x, y));
  const ra = restoA();
  const rb = restoB();
  for (const x of ra) {
    const cs = cand(x, rb);
    if (cs.length !== 1) continue;
    const y = cs[0]!;
    if (usadasB.has(y.id) || cand(y, ra).length !== 1) continue;
    parejas.push({ a: x.id, b: y.id, por: 'forma', dudoso: true });
    usadasA.add(x.id);
    usadasB.add(y.id);
  }
  const porForma = (vs: readonly VistaComparable[]) => {
    const m = new Map<string, VistaComparable[]>();
    for (const v of vs) (m.get(forma(v)) ?? m.set(forma(v), []).get(forma(v))!).push(v);
    return m;
  };
  const fb = porForma(restoB());
  for (const [f, xs] of porForma(restoA())) {
    const ys = fb.get(f) ?? [];
    if (ys.length !== xs.length) continue;
    xs.forEach((x, i) => {
      parejas.push({ a: x.id, b: ys[i]!.id, por: 'orden', dudoso: true });
      usadasA.add(x.id);
      usadasB.add(ys[i]!.id);
    });
  }
  return {
    parejas,
    soloA: a.filter((v) => !usadasA.has(v.id)).map((v) => v.id),
    soloB: b.filter((v) => !usadasB.has(v.id)).map((v) => v.id),
  };
}
